import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import logo from "../../assets/images/logo.png";
import Seo from "../../components/Seo";
import { Avatar, EmptyState, Modal, PageHeader, Spinner } from "../../components/lawyer/LawyerUi";
import { isBlankFile, plainText } from "../../services/lawyerApi";
import { apiErrorMessage, userService } from "../../services/userApi";

// Where a notification leads, based on the data payload the backend attaches.
const targetOf = (data) => {
  const id = data?.id;
  switch (data?.type) {
    case "chat":
      return id ? `/user/chat/${id}` : "/user/chat";
    case "quick_question":
      return id ? `/user/requests/${id}` : "/user/requests";
    case "case":
      return "/user/requests";
    case "service":
    case "user_service":
    case "package":
      return "/user/my-lawyers";
    case "consultation_call":
    case "call":
      return "/user/call-history";
    default:
      return null;
  }
};

const UserNotifications = () => {
  const navigate = useNavigate();
  const { refreshProfile } = useOutletContext();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearing, setClearing] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await userService.getNotifications();
      if (!res?.data?.status) throw new Error(res?.data?.message);
      setItems(res.data.data?.notifications || []);
    } catch (err) {
      toast.error(apiErrorMessage(err, err?.message || "Unable to load notifications."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const open = (n) => {
    if (Number(n.is_read) === 0) {
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_read: 1 } : x)));
      userService
        .markNotificationRead(n.id)
        .then(refreshProfile)
        .catch(() => {});
    }
    const to = targetOf(n.data);
    if (to) navigate(to);
  };

  const clearAll = async () => {
    setClearing(true);
    try {
      const res = await userService.clearNotifications();
      if (!res?.data?.status) throw new Error(res?.data?.message);
      toast.success(res.data.message || "Notifications cleared");
      setConfirmClear(false);
      // Platform-wide announcements aren't deleted by the API, so reload rather than empty the list.
      await load();
      refreshProfile();
    } catch (err) {
      toast.error(apiErrorMessage(err, err?.message || "Unable to clear notifications."));
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="lwp-page lwp-page-narrow">
      <Seo title="Notifications | Legal Platform" noindex />
      <PageHeader
        title="Notifications"
        backTo="/user/profile"
        actions={
          items.length > 0 && (
            <button type="button" className="lwp-text-btn" onClick={() => setConfirmClear(true)}>
              Clear
            </button>
          )
        }
      />

      {loading ? (
        <Spinner />
      ) : items.length === 0 ? (
        <EmptyState icon="bi-bell-slash" text="You're all caught up." />
      ) : (
        <ul className="lwp-card-list">
          {items.map((n) => {
            const picture = n.data?.picture;
            const clickable = !!targetOf(n.data);
            return (
              <li key={n.id}>
                <button
                  type="button"
                  className={`lwp-notif${Number(n.is_read) === 0 ? " unread" : ""}${clickable ? "" : " static"}`}
                  onClick={() => open(n)}
                >
                  <span className="lwp-notif-head">
                    {picture && !isBlankFile(picture) ? (
                      <Avatar src={picture} size={60} />
                    ) : (
                      <span className="lwp-activity-avatar lwp-notif-logo">
                        <img src={logo} alt="" />
                      </span>
                    )}
                    <strong>{n.title}</strong>
                    <time dateTime={n.created_at}>{moment(n.created_at).fromNow(true)}</time>
                  </span>
                  {plainText(n.message) && <span className="lwp-notif-msg">{plainText(n.message)}</span>}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <Modal
        open={confirmClear}
        onClose={() => !clearing && setConfirmClear(false)}
        title="Clear notifications"
        footer={
          <div className="lwp-modal-actions">
            <button type="button" className="lwp-btn lwp-btn-outline" onClick={() => setConfirmClear(false)} disabled={clearing}>
              Cancel
            </button>
            <button type="button" className="lwp-btn" onClick={clearAll} disabled={clearing}>
              {clearing ? <span className="spinner-border spinner-border-sm" /> : "Clear all"}
            </button>
          </div>
        }
      >
        <p className="lwp-modal-text">Remove all your notifications? This can't be undone.</p>
      </Modal>
    </div>
  );
};

export default UserNotifications;
